import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { PageHeader, StatCard, Btn, Avatar, Badge, Card } from '../components/UI';

export default function Notifications() {
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    api.getRebookEligible()
      .then(r => setEligible(r.data || []))
      .catch(() => toast.error('Failed to load alerts'))
      .finally(() => setLoading(false));
  }, []);

  const overdue45 = eligible.filter(c => !c.daysSince || c.daysSince >= 45);
  const overdue30 = eligible.filter(c => c.daysSince && c.daysSince >= 30 && c.daysSince < 45);
  const never     = eligible.filter(c => c.daysSince === null);

  const filtered = filter === 'urgent' ? overdue45 : filter === 'never' ? never : eligible;

  const filterTabs = [
    { key:'all',    label:'All Alerts',     count:eligible.length },
    { key:'urgent', label:'Urgent (45+)',   count:overdue45.length },
    { key:'30',     label:'30-44 Days',     count:overdue30.length },
    { key:'never',  label:'Never Booked',   count:never.length },
  ];

  return (
    <div>
      <PageHeader
        title="Rebooking Alerts"
        subtitle="Customers eligible for rebooking — 30+ days since last delivery"
      />

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
        <StatCard label="Total Eligible"    value={eligible.length}  icon="🔔" color={eligible.length > 0 ? 'var(--brand)' : undefined}          sub="30+ days since booking" />
        <StatCard label="Overdue (45+ days)" value={overdue45.length} icon="🚨" color={overdue45.length > 0 ? 'var(--accent-red)' : undefined}    sub="Urgent attention needed" />
        <StatCard label="30-44 Days"         value={overdue30.length} icon="⚠️" color={overdue30.length > 0 ? 'var(--accent-amber)' : undefined}  sub="Approaching overdue" />
        <StatCard label="Never Booked"       value={never.length}     icon="📭" color={never.length > 0 ? 'var(--accent-purple)' : undefined}     sub="No booking history" />
      </div>

      {/* Filter tabs — pill style, wraps on mobile */}
      <div className="au d1" style={{ display:'flex', gap:6, marginBottom:18, flexWrap:'wrap' }}>
        {filterTabs.map(({ key, label, count }) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            padding:'7px 16px', borderRadius:'var(--radius-full)',
            border:`1px solid ${filter === key ? 'var(--brand)' : 'var(--border-default)'}`,
            background: filter === key ? 'var(--brand-dim)' : 'transparent',
            color: filter === key ? 'var(--brand)' : 'var(--text-secondary)',
            fontSize:12.5, fontWeight:600, cursor:'pointer', transition:'var(--transition)',
            display:'flex', alignItems:'center', gap:7,
          }}>
            {label}
            {count > 0 && (
              <span style={{
                background: filter === key ? 'var(--brand)' : 'var(--bg-elevated)',
                color: filter === key ? 'white' : 'var(--text-muted)',
                borderRadius:99, padding:'1px 7px', fontSize:11,
              }}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {[...Array(4)].map((_,i) => (
            <div key={i} className="skeleton" style={{ height:80, borderRadius:'var(--radius-lg)' }} />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <Card style={{ textAlign:'center', padding:'52px 20px' }}>
          <div style={{ fontSize:44, marginBottom:14 }}>✅</div>
          <div style={{ fontSize:16, fontWeight:700, color:'var(--text-primary)', marginBottom:6 }}>All caught up!</div>
          <div style={{ fontSize:13, color:'var(--text-muted)' }}>No customers require attention in this category.</div>
        </Card>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.map((c, i) => {
          const urgent  = !c.daysSince || c.daysSince >= 45;
          const neverB  = c.daysSince === null;
          const accentColor = urgent ? 'var(--accent-red)' : neverB ? 'var(--accent-purple)' : 'var(--accent-amber)';

          return (
            <div key={c.id} className="au" style={{ animationDelay:`${i * 0.035}s`,
              display:'flex', alignItems:'center', gap:14, padding:'14px 18px',
              background:'var(--bg-surface)',
              border:`1px solid ${urgent ? 'rgba(239,68,68,.18)' : neverB ? 'rgba(168,85,247,.15)' : 'var(--border-subtle)'}`,
              borderRadius:'var(--radius-lg)', transition:'var(--transition)', flexWrap:'wrap',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-surface)'}>

              {/* Status dot */}
              <div style={{
                width:9, height:9, borderRadius:'50%', background:accentColor, flexShrink:0,
                animation: urgent ? 'pulse2 1.2s infinite' : undefined,
              }} />

              <Avatar name={c.name} size={38} />

              {/* Main info */}
              <div style={{ flex:1, minWidth:160 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', marginBottom:3 }}>{c.name}</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', display:'flex', gap:12, flexWrap:'wrap' }}>
                  <span>📞 {c.phone}</span>
                  <span style={{ fontFamily:'var(--font-mono)' }}>🔗 {c.connectionNo}</span>
                  <span>🛢️ {c.cylinderType?.split(' ').slice(0,2).join(' ')}</span>
                </div>
              </div>

              {/* Days badge */}
              <div style={{ textAlign:'right', flexShrink:0 }}>
                {neverB ? (
                  <Badge type="purple" dot>Never booked</Badge>
                ) : (
                  <div>
                    <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:4 }}>Last: {c.lastBooking?.bookingDate}</div>
                    <Badge type={urgent ? 'danger' : 'warning'} dot>
                      <strong style={{ fontSize:13 }}>{c.daysSince}</strong> days ago
                    </Badge>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                <Btn size="sm" variant="ghost" onClick={() => navigate('/customers')}>View</Btn>
                <Btn size="sm" onClick={() => navigate('/bookings')}>📋 Book Now</Btn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
